import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormBuilder, FormGroup, ReactiveFormsModule } from "@angular/forms";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { Observable } from "rxjs";

// Material MDC Imports
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatSelectModule } from "@angular/material/select";
import { MatInputModule } from "@angular/material/input";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { MatButtonModule } from "@angular/material/button";
import { MatIconModule } from "@angular/material/icon";
import { MatTooltipModule } from "@angular/material/tooltip";
import { MatCardModule } from "@angular/material/card";
import { MatDividerModule } from "@angular/material/divider";
import { MatChipsModule } from "@angular/material/chips";

import { IGeofencingSearchValidation, SearchFilters } from "../model";
import { distinctUntilChanged, map } from "rxjs/operators";
import { GeofencingSearchFilterService, SearchQueryService } from "../services/search";

/**
 * Search filters panel component with slide animation and full Material Design integration
 */
@Component({
  selector: "app-compound-search-settings",
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: "./search-settings.component.html",
  styleUrls: ["./search-settings.component.scss"],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatSelectModule,
    MatInputModule,
    MatCheckboxModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatCardModule,
    MatDividerModule,
    MatChipsModule,
  ],
})
export class SearchSettingsComponent implements OnInit {
  formBuilder = inject(FormBuilder);

  searchQueryService = inject(SearchQueryService);

  cdr = inject(ChangeDetectorRef);

  /** Shared owner of the geofencing dialog/apply/remove workflow, also used by the header search bar. */
  public readonly geofencingFilterService = inject(GeofencingSearchFilterService);

  /** Whether the panel is visible */
  @Input() isVisible = false;

  /** Event emitted when panel visibility changes */
  @Output() visibilityChange = new EventEmitter<boolean>();

  /** Reactive form for all filter controls */
  filtersForm: FormGroup;

  /** Available page size options */
  pageSizeOptions = [20, 50, 100, 200, 500];

  maxResults: number = 500;

  private destroyRef = inject(DestroyRef);

  /** Emits true if selection should be disabled (loading or results present) */
  isSelectionDisabled$: Observable<boolean>;

  /**
   * Geofencing precondition of the embeddings search: an area is mandatory and must stay under
   * the maximum the API can process. Drives the area readout and warning in this panel.
   */
  geofencingValidation$: Observable<IGeofencingSearchValidation> = this.searchQueryService.geofencingValidation$;

  ngOnInit(): void {
    this.createForm();
    this.subscribeToSearchFilters();
    this.setupFormValueChanges();
    this.setupSearchStateSubscription();
  }

  /**
   * Creates the reactive form with all filter controls
   */
  private createForm(): void {
    this.filtersForm = this.formBuilder.group({
      wkt: [""],
      pageSize: [100],
      maxResults: [this.maxResults],
      bufferRadius: [null],
    });
  }

  setupSearchStateSubscription() {
    // Compute disabled state: loading or results present
    this.isSelectionDisabled$ = this.searchQueryService.searchState$.pipe(
      map((state) => !!state?.loading || (Array.isArray(state?.results) && state.results.length > 0)),
      distinctUntilChanged(),
    );

    // Reactively enable/disable form
    this.isSelectionDisabled$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((disabled) => {
      if (disabled) {
        this.filtersForm.disable({ emitEvent: false });
      } else {
        this.filtersForm.enable({ emitEvent: false });
      }
      this.cdr.markForCheck();
    });
  }

  /**
   * Subscribes to search filters changes from the service
   */
  private subscribeToSearchFilters(): void {
    this.searchQueryService.searchFilters$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((filters) => {
      this.filtersForm.patchValue(filters, { emitEvent: false });
      this.cdr.detectChanges();
    });
  }

  /**
   * Sets up form value changes to update the search service
   */
  private setupFormValueChanges(): void {
    this.filtersForm.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((formValue) => {
      const filters: SearchFilters = {
        wkt: formValue.wkt,
        "buffer-meter": formValue.bufferRadius,
        pageSize: formValue.pageSize,
        maxResults: formValue.maxResults,
      };
      this.searchQueryService.updateSearchFiltersAndState(filters);
    });
  }

  /**
   * Opens the geofencing configuration dialog — actual workflow lives in
   * {@link GeofencingSearchFilterService}, shared with the header search bar.
   */
  openGeofencingDialog(): void {
    // Close settings panel during geofencing selection, reopen once the dialog flow settles.
    this.visibilityChange.emit(false);
    this.geofencingFilterService.openDialog({
      onDialogClosed: () => this.visibilityChange.emit(true),
    });
  }

  /**
   * Removes the currently applied geofencing filter.
   */
  removeGeofencingFilter(): void {
    this.geofencingFilterService.removeGeofencingFilter();
  }
}
